import { EyeOff } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { CustomInput } from "@/components/forms/customInput"
import { EyeIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { SocialLoginButtons } from "@/features/auth/components/SocialLoginButtons"
import { useAuthForm } from "@/features/auth/hooks/useAuthForm"
import { type LoginFormData, loginSchema } from "@/features/auth/schemas/auth.schema"
import { useAuthStore } from "@/features/auth/store/authStore"
import { saveCurrentLocationAsReturnTo } from "@/features/auth/utils/authReturnTo"

interface LoginModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function LoginModal({ open, onOpenChange }: LoginModalProps) {
  const { t } = useTranslation("auth")
  const navigate = useNavigate()
  const login = useAuthStore(state => state.login)
  const [showPassword, setShowPassword] = useState(false)

  const { form, handleSubmit, isSubmitting, serverError, clearServerError } = useAuthForm(
    loginSchema,
    async (data: LoginFormData) => {
      const response = await login(data.email, data.password)

      onOpenChange(false)

      // The modal usually opens on a content page (e.g. a shared ritual); the
      // capture is a no-op on home/excluded routes. If login still needs
      // verify/onboarding, remember that page so the flow returns here.
      if (!response.user.emailVerified) {
        saveCurrentLocationAsReturnTo()
        navigate("/verify-email", { state: { email: data.email } })
        return
      }

      if (!response.user.onboardingCompleted) {
        saveCurrentLocationAsReturnTo()
        navigate("/onboarding/permissions")
      } else {
        // Fully authenticated already -- reload in place to reveal gated content.
        window.location.reload()
      }
    },
  )

  const emailRegister = form.register("email")
  const passwordRegister = form.register("password")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="bg-[#FFFFFF0D] backdrop-blur-xl border border-[#F2F0EB4D] rounded-tr-[30px] rounded-bl-[30px] max-w-[640px] p-7"
        overlayClass="bg-[#020202D9]"
      >
        <DialogTitle className="sr-only">Login to Rituality</DialogTitle>
        <form
          onSubmit={e => {
            e.preventDefault()
            handleSubmit(e)
          }}
        >
          <div className="space-y-6 px-5 sm:px-10 md:px-27">
            <div className="text-center mb-10 mt-5">
              <h2 className="text-2xl font-[500] text-primary font-base font-semibold">
                Login to Rituality
              </h2>
            </div>

            <div className="space-y-2">
              <div className="space-y-5">
                <CustomInput
                  label={t("login.emailLabel")}
                  type="email"
                  placeholder={t("login.emailPlaceholder")}
                  required={false}
                  {...emailRegister}
                  error={form.formState.errors.email?.message as string | undefined}
                  disabled={isSubmitting}
                  onChange={e => {
                    emailRegister.onChange?.(e)
                    clearServerError()
                  }}
                  className="pb-1"
                />

                <div className="relative">
                  <CustomInput
                    label={t("login.passwordLabel")}
                    type={showPassword ? "text" : "password"}
                    required={false}
                    placeholder={t("login.passwordPlaceholder")}
                    {...passwordRegister}
                    error={
                      (form.formState.errors.password?.message || serverError) as string | undefined
                    }
                    disabled={isSubmitting}
                    onChange={e => {
                      passwordRegister.onChange?.(e)
                      clearServerError()
                    }}
                    className="pb-1 pr-8"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center text-body cursor-pointer"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff size={17} className="text-primary" />
                    ) : (
                      <EyeIcon size={17} />
                    )}
                  </button>
                </div>
              </div>

              <div className="text-center w-full pb-0 cursor-pointer flex items-end justify-end mt-0">
                <button
                  type="button"
                  className="text-[13px] text-body bg-transparent border-none p-0 me-1 leading-[25px] cursor-pointer"
                  onClick={() => {
                    onOpenChange(false)
                    navigate("/forgot-password")
                  }}
                >
                  Trouble signing in? <span className="font-semibold">Reset password</span>
                </button>
              </div>

              <div className="py-2 mt-3.5">
                <Button
                  type="submit"
                  variant="primary-white"
                  size="md"
                  className="uppercase"
                  isLoading={isSubmitting}
                  fullWidth
                >
                  Login
                </Button>
              </div>

              <SocialLoginButtons isLoading={isSubmitting} />
            </div>
          </div>

          <div className="text-center pt-12 w-full space-y-4.5">
            <p className="font-semibold text-[14px]">Don't have an account?</p>
            <div className="mx-auto sm:px-0 px-5">
              <Button
                type="button"
                variant="outline"
                size="md"
                className="uppercase w-full sm:w-[365px]  hover:!bg-transparent hover:!text-[#F2F0EB] hover:!border-[#F2F0EB]"
                onClick={() => {
                  // Preserve the current page (e.g. the shared ritual) across the
                  // signup -> verify -> onboarding chain so the user lands back here.
                  saveCurrentLocationAsReturnTo()
                  onOpenChange(false)
                  navigate("/signup")
                }}
              >
                Sign Up
              </Button>
            </div>
            <p className="text-[14px] mt-8 ms-2 text-body leading-[25px]">
              {" "}
              By continuing, you agree to our{" "}
              <a
                href="https://myrituality.com/terms/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold"
              >
                Terms & Conditions
              </a>{" "}
              and{" "}
              <a
                href="https://myrituality.com/privacy/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
